import { type ReactNode, useEffect, useMemo, useState } from "react";
import { isWebView } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import fetchHeaderFooter, {
  type HeaderFooterResponse,
} from "@/api/headerFooter/fetchHeaderFooter";
import { FooterComponent } from "@/components/Footer";
import { HeaderComponent } from "@/components/Header";
import { PointsBanner } from "@/components/shared/PointsBanner";
import { useGetPointsBalance } from "@/modules/account";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import "./Layout.scss";
import { FunnelNavigation } from "@/pages/Homepage/FunnelNavigation/FunnelNavigation";
import { NoticeBanner } from "@/pages/Homepage/NoticeBanner";

interface LayoutProps {
  children: ReactNode;
  isMaintenanceMode?: boolean;
}

export const Layout = ({
  children,
  isMaintenanceMode = false,
}: LayoutProps) => {
  const { getPointsBalance, pointsBalance } = useGetPointsBalance();
  const isInWebView = useMemo(() => isWebView(), []);
  const { language } = useI18nContext();
  const { isExperimentVariant } = useExperiment();
  const isHeaderFooterEnabled = isExperimentVariant(
    Experiments.HEADER_FOOTER.name,
    Experiments.HEADER_FOOTER.variants.available
  );
  const [headerFooterData, setHeaderFooterData] =
    useState<HeaderFooterResponse | null>(null);
  const [headerFooterError, setHeaderFooterError] = useState(false);

  // Fetch points balance once on layout mount
  useEffect(() => {
    if (pointsBalance == null) {
      getPointsBalance(true);
    }
  }, [pointsBalance, getPointsBalance]);

  // Fetch header/footer once for both components
  useEffect(() => {
    if (isHeaderFooterEnabled) {
      const fetchResources = async () => {
        try {
          const response = await fetchHeaderFooter();
          setHeaderFooterData(response);
        } catch (e) {
          console.error("Failed to fetch header/footer", e);
          setHeaderFooterError(true);
        }
      };
      fetchResources();
    }
  }, [isHeaderFooterEnabled, language]);

  return (
    <div className="rbc-layout">
      <div className="rbc-layout__header">
        {!isInWebView && (
          <>
            <HeaderComponent
              headerData={headerFooterData?.header}
              hasError={headerFooterError}
            />
            {!isMaintenanceMode && <PointsBanner />}
          </>
        )}
        {!isMaintenanceMode && <FunnelNavigation />}
        {!isMaintenanceMode && <NoticeBanner />}
      </div>

      <div className="rbc-layout__content">{children}</div>
      <div className="rbc-layout__footer">
        <FooterComponent
          footerData={headerFooterData?.footer}
          hasError={headerFooterError}
        />
      </div>
    </div>
  );
};
